import React from "react";
import { Link } from "react-router-dom";
import {
  Users,
  MapPin,
  StickyNote,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Globe,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { shortAddress } from "@/lib/address";

function NotOnMapPill({ b, isAdmin, onFixPin, fixing }) {
  if (!isAdmin || !onFixPin) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-500"
        title="Address on file, but it has no map pin yet — an admin can fix it from this row."
      >
        <AlertTriangle className="h-3 w-3" /> Not on map
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onFixPin(b)}
      disabled={fixing}
      className="inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 hover:bg-amber-100 disabled:opacity-60 transition-colors"
      title="Address on file but no map pin. Click to look it up on Google and pin it."
      data-testid={`brokerage-fix-pin-${b.id}`}
    >
      {fixing ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : <AlertTriangle className="h-3 w-3 shrink-0" />}
      {fixing ? (
        "Looking up…"
      ) : (
        <>
          <span className="sm:hidden">Fix map pin</span>
          <span className="hidden sm:inline">Not on map — Fix here</span>
        </>
      )}
    </button>
  );
}

export default function BrokerageList({ rows, selectedIds, onToggle, onEdit, onDetail, onDelete, onFixPin, fixingId, isAdmin, expanded, onExpand, realtorsById, extractCity }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-500 text-center">
        No matches. Adjust your search or city filter.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="brokerages-list">
      {rows.map((b) => {
        const isExpanded = expanded.has(b.id);
        const realtors = realtorsById[b.id] || [];
        const isMapped = b.geocode_status === "ok";
        const city = b.city || extractCity(b.office_address);
        const hasNotes = (b.interaction_log && b.interaction_log.length > 0) || b.notes;
        return (
          <li
            key={b.id}
            className={`rounded-md border bg-white overflow-hidden ${
              selectedIds.has(b.id) ? "border-emerald-400 shadow-sm" : "border-slate-200"
            }`}
            data-testid={`brokerage-row-${b.id}`}
          >
            <div className="flex items-start gap-3 p-3">
              <Checkbox
                checked={selectedIds.has(b.id)}
                onCheckedChange={() => onToggle(b.id)}
                className="mt-1 shrink-0"
                disabled={!isMapped}
                data-testid={`brokerage-row-checkbox-${b.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => onDetail(b)}
                    className="font-semibold text-slate-900 text-sm sm:text-base hover:text-[hsl(var(--brand))] transition-colors text-left"
                    data-testid={`brokerage-detail-btn-${b.id}`}
                  >
                    {b.name}
                  </button>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs min-w-0 max-w-full">
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                      <Users className="h-3 w-3" /> {b.realtor_count || 0}
                    </span>
                    {city ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                        <MapPin className="h-3 w-3" /> {city}
                      </span>
                    ) : null}
                    {hasNotes ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">
                        <StickyNote className="h-3 w-3" /> Notes
                      </span>
                    ) : null}
                    {b.office_address_source === "manual" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
                        Manual
                      </span>
                    ) : null}
                    {!b.office_address ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-slate-600">
                        <AlertTriangle className="h-3 w-3" /> No address
                      </span>
                    ) : !isMapped ? (
                      <NotOnMapPill b={b} isAdmin={isAdmin} onFixPin={onFixPin} fixing={fixingId === b.id} />
                    ) : null}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-1 min-w-0">
                  {b.office_address ? (
                    <span className="flex items-center gap-1 min-w-0">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate" title={b.office_address}>
                        {shortAddress(b.office_address)}
                      </span>
                    </span>
                  ) : (
                    <span className="italic text-slate-400">No address on file</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
                  <button
                    type="button"
                    onClick={() => onDetail(b)}
                    className="inline-flex items-center gap-1 text-[hsl(var(--brand))] hover:underline font-medium"
                    data-testid={`brokerage-open-detail-${b.id}`}
                  >
                    <StickyNote className="h-3.5 w-3.5" /> Details
                  </button>
                  <button
                    type="button"
                    onClick={() => onExpand(b)}
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                    data-testid={`brokerage-expand-${b.id}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    {isExpanded ? "Hide realtors" : "View realtors"}
                  </button>
                  {b.website_url ? (
                    <a
                      href={b.website_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-slate-500 hover:underline"
                    >
                      <Globe className="h-3.5 w-3.5" /> Website
                    </a>
                  ) : null}
                  {isAdmin ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onEdit(b)}
                        className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                        data-testid={`brokerage-edit-${b.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(b)}
                        className="ml-auto inline-flex items-center gap-1 text-slate-400 hover:text-red-600 transition-colors"
                        data-testid={`brokerage-delete-${b.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : null}
                </div>
                {isExpanded ? (
                  <div className="mt-2 rounded border border-slate-100 bg-slate-50 p-2">
                    {realtors.length === 0 ? (
                      <div className="text-xs text-slate-500 italic">No realtors loaded.</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {realtors.slice(0, 40).map((r) => (
                          <Link
                            key={r.id}
                            to={`/realtors/${r.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700 hover:border-slate-300"
                          >
                            {r.name || `${r.first_name} ${r.last_name}`.trim() || "—"}
                          </Link>
                        ))}
                        {realtors.length > 40 ? (
                          <span className="text-[11px] text-slate-500 self-center">
                            +{realtors.length - 40} more
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
